import React from "react";
import "./Scoreboard.css";

/**
 * Building blocks for the scoreboard's goal, penalty and lineup lists.
 *
 * These render as stacked rows rather than a table. A table of time, player,
 * detail and two labelled buttons is wider than a phone in portrait, which
 * forced the whole card to scroll sideways; a row wraps its detail onto a
 * second line instead and keeps the actions icon-sized.
 */

/** A square, label-free button — icons keep the action column narrow. */
export function IconButton({
  icon,
  label,
  onClick,
  variant = "default",
}: {
  /** Bootstrap Icons name without the `bi-` prefix, e.g. `pencil`. */
  icon: string;
  /** Accessible name; also the tooltip, since there is no visible text. */
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
}) {
  return (
    <button
      type="button"
      className={`sb-icon-btn${
        variant === "danger" ? " sb-icon-btn--danger" : ""
      }`}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      <i className={`bi bi-${icon}`} aria-hidden="true" />
    </button>
  );
}

/** A period heading and the entries recorded in it. */
export function EventGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sb-group">
      <h6 className="sb-group-title">{title}</h6>
      {children}
    </div>
  );
}

/**
 * One recorded event. `lead` is the clock time where there is one, `primary`
 * the player it belongs to, and `secondary` the supporting detail — assists,
 * the penalty called, the substitute — which drops to its own line.
 */
export function EventRow({
  lead,
  primary,
  secondary,
  onEdit,
  onDelete,
  editLabel,
  deleteLabel,
}: {
  lead?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  editLabel: string;
  deleteLabel: string;
}) {
  return (
    <div className="sb-row">
      <div className="sb-row-main">
        <div className="sb-row-top">
          {lead != null && <span className="sb-row-lead">{lead}</span>}
          <span className="sb-row-primary">{primary}</span>
        </div>
        {secondary && <div className="sb-row-secondary">{secondary}</div>}
      </div>
      <div className="sb-row-actions">
        <IconButton icon="pencil" label={editLabel} onClick={onEdit} />
        <IconButton
          icon="trash"
          label={deleteLabel}
          onClick={onDelete}
          variant="danger"
        />
      </div>
    </div>
  );
}

/** Placeholder shown in a section that has no entries yet. */
export function EventEmpty({ children }: { children: React.ReactNode }) {
  return <p className="sb-empty">{children}</p>;
}
